import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { useAuth } from '@/store/auth'
import { BrandLockup } from '@/components/Logo'
import { ApiRequestError } from '@/lib/api'

type Mode = 'login' | 'register'

export default function Login() {
  const navigate = useNavigate()
  const login = useAuth((s) => s.login)
  const register = useAuth((s) => s.register)

  const [mode, setMode] = useState<Mode>('login')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      if (mode === 'login') await login(email, password)
      else await register(email, password, name || undefined)
      navigate('/', { replace: true })
    } catch (err) {
      // Surface the server's own validation messages — "Password must be at
      // least 10 characters" is more useful than "registration failed", and
      // the server already decided what is safe to say.
      const detail = err instanceof ApiRequestError ? err.detail : undefined
      const issues = (detail?.issues as { message: string }[] | undefined)?.map((i) => i.message)
      setError(issues?.join(' ') ?? (err as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6 texture-dots">
      <div className="w-full max-w-sm">
        {/*
          The front door, so the lockup is the whole of the brand rather than
          the icon with the name under it — the icon stacked above a lockup
          that already contains it reads as the logo twice.
        */}
        <div className="mb-6 flex flex-col items-center gap-3">
          <BrandLockup height={46} className="anim-float" />
          <p className="text-[11px] text-muted-foreground">Options execution terminal</p>
        </div>

        <form onSubmit={submit} className="qbar-panel space-y-3 p-6 shadow-card-soft">
          <div className="flex rounded-md border border-border bg-secondary p-0.5">
            {(['login', 'register'] as const).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => {
                  setMode(m)
                  setError(null)
                }}
                data-state={mode === m ? 'active' : 'inactive'}
                className="qbar-tab-trigger flex-1"
              >
                {m === 'login' ? 'Sign in' : 'Create account'}
              </button>
            ))}
          </div>

          {mode === 'register' && (
            <label className="block">
              <span className="qbar-meta">Name</span>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="name"
                className="mt-1 h-9 w-full rounded-md border border-input bg-background px-2.5 text-[13px] text-foreground outline-none focus:border-ring"
              />
            </label>
          )}

          <label className="block">
            <span className="qbar-meta">Email</span>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              autoFocus
              className="mt-1 h-9 w-full rounded-md border border-input bg-background px-2.5 text-[13px] text-foreground outline-none focus:border-ring"
            />
          </label>

          <label className="block">
            <span className="qbar-meta">Password</span>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
              className="mt-1 h-9 w-full rounded-md border border-input bg-background px-2.5 text-[13px] text-foreground outline-none focus:border-ring"
            />
            {mode === 'register' && (
              <span className="mt-1 block text-[10px] text-muted-foreground">
                At least 10 characters. Length beats symbols.
              </span>
            )}
          </label>

          {error && (
            <p
              role="alert"
              className="rounded-md border border-destructive/40 bg-destructive/10 px-2.5 py-2 text-[11px] text-destructive"
            >
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={busy}
            className="flex h-9 w-full items-center justify-center gap-2 rounded-md bg-primary text-[12px] font-bold tracking-wide text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-50"
          >
            {busy && <Loader2 size={14} className="animate-spin" />}
            {mode === 'login' ? 'SIGN IN' : 'CREATE ACCOUNT'}
          </button>

          {mode === 'register' && (
            <p className="text-center text-[10px] text-muted-foreground/70">
              The first account created owns this deployment.
            </p>
          )}
        </form>
      </div>
    </div>
  )
}
